import { useState, useEffect } from 'react';
import { useLang } from '../lib/lang';
import { getSettings, getLegalPagesPublic } from '../lib/api';
import { useBranding } from '../lib/branding';
import { Separator } from './ui/separator';
import InlineEditable from './InlineEditable';
import { Instagram, Facebook, Twitter, Mail, MapPin, Shield, Youtube, Linkedin } from 'lucide-react';

const LOGO_URL_DEFAULT = '/assets/winluxor-logo.png';

// TikTok SVG (lucide doesn't have it natively at small sizes)
const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005.8 20.1a6.34 6.34 0 0010.86-4.43V8.93a8.16 8.16 0 004.77 1.52V7a4.85 4.85 0 01-1.84-.31z" />
  </svg>
);

const PLATFORMS = [
  { key: 'instagram', label: 'Instagram', Icon: Instagram, defaultUrl: 'https://instagram.com/winluxor' },
  { key: 'facebook', label: 'Facebook', Icon: Facebook, defaultUrl: 'https://facebook.com/winluxor' },
  { key: 'twitter', label: 'Twitter / X', Icon: Twitter, defaultUrl: 'https://x.com/winluxor' },
  { key: 'tiktok', label: 'TikTok', Icon: TikTokIcon, defaultUrl: 'https://tiktok.com/@winluxor' },
  { key: 'youtube', label: 'YouTube', Icon: Youtube, defaultUrl: 'https://youtube.com/@winluxor' },
  { key: 'linkedin', label: 'LinkedIn', Icon: Linkedin, defaultUrl: 'https://linkedin.com/company/winluxor' },
];

export default function Footer() {
  const { t, lang } = useLang();
  const branding = useBranding();
  const LOGO_URL = branding.logo_main_url || LOGO_URL_DEFAULT;
  const [socialLinks, setSocialLinks] = useState({});
  const [legalPages, setLegalPages] = useState([]);
  const [navFlags, setNavFlags] = useState({});

  useEffect(() => {
    getSettings()
      .then((r) => {
        setSocialLinks(r.data.settings?.social_links || {});
        setNavFlags(r.data.settings?.feature_flags || {});
      })
      .catch(() => {});
    getLegalPagesPublic()
      .then((r) => setLegalPages(r.data?.pages || []))
      .catch(() => setLegalPages([]));
  }, []);

  const visibleSocials = PLATFORMS.filter((p) => {
    const cfg = socialLinks[p.key];
    return cfg && cfg.enabled === true && cfg.url && cfg.url.trim().length > 0;
  });

  return (
    <footer className="relative bg-rolex-green border-t border-[hsl(var(--gold)/0.25)] mt-auto" data-testid="site-footer">
      <div className="absolute inset-0 noise-overlay opacity-50 pointer-events-none" />
      <div className="divider-gold-thick" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          <div className="md:col-span-1">
            <img src={LOGO_URL} alt="WINLUXOR" className="h-20 mb-5 rounded-md" />
            <p className="text-sm text-cream/60 leading-relaxed mb-5"><InlineEditable tKey="footerDesc" multiline>{t('footerDesc')}</InlineEditable></p>
            <div className="flex items-center gap-2 text-xs text-gold/80">
              <Shield className="w-3.5 h-3.5" /> Licensed & Regulated UK Operator
            </div>
          </div>
          <div>
            <h4 className="font-display font-semibold text-sm uppercase tracking-[0.2em] mb-5 text-gold"><InlineEditable tKey="quickLinks">{t('quickLinks')}</InlineEditable></h4>
            <ul className="space-y-3 text-sm text-cream/60">
              <li><a href="/#raffles" className="hover:text-gold transition-colors"><InlineEditable tKey="activeDraws">{t('activeDraws')}</InlineEditable></a></li>
              <li><a href="/#how-it-works" className="hover:text-gold transition-colors"><InlineEditable tKey="howItWorks">{t('howItWorks')}</InlineEditable></a></li>
              <li><a href="/#winners" className="hover:text-gold transition-colors"><InlineEditable tKey="recentWinners">{t('recentWinners')}</InlineEditable></a></li>
              <li><a href="/#faq" className="hover:text-gold transition-colors">FAQ</a></li>
              {navFlags.nav_vip !== false && (
                <li><a href="/vip" className="hover:text-gold transition-colors"><InlineEditable tKey="vip">{t('vip')}</InlineEditable></a></li>
              )}
            </ul>
          </div>
          <div>
            <h4 className="font-display font-semibold text-sm uppercase tracking-[0.2em] mb-5 text-gold"><InlineEditable tKey="legal">{t('legal')}</InlineEditable></h4>
            <ul className="space-y-3 text-sm text-cream/60">
              {legalPages.length === 0 && (
                <li className="text-xs italic text-cream/40">No legal pages enabled</li>
              )}
              {legalPages.map((p) => (
                <li key={p.slug}>
                  <a
                    href={`/${p.slug}`}
                    className="hover:text-gold transition-colors"
                    data-testid={`footer-legal-${p.slug}-link`}
                  >
                    {lang === 'fr' ? (p.title_fr || p.title_en) : (p.title_en || p.title_fr)}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="font-display font-semibold text-sm uppercase tracking-[0.2em] mb-5 text-gold"><InlineEditable tKey="support">{t('support')}</InlineEditable></h4>
            <ul className="space-y-3 text-sm text-cream/60">
              <li>
                <a href={`mailto:${socialLinks?.contact_email || 'support@winluxor.com'}`} className="hover:text-gold transition-colors flex items-center gap-1.5" data-testid="footer-support-link">
                  <Mail className="w-3.5 h-3.5" />
                  <InlineEditable tKey="contactEmail">{socialLinks?.contact_email || 'support@winluxor.com'}</InlineEditable>
                </a>
              </li>
              <li className="flex items-start gap-1.5 text-cream/55">
                <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <InlineEditable tKey="contactAddress" multiline>{socialLinks?.contact_address || '123 Luxury Lane, London W1, UK'}</InlineEditable>
              </li>
              <li className="text-xs flex items-center gap-1.5 text-cream/45 ml-5">
                <InlineEditable tKey="openingHours">Monday — Friday, 9am – 6pm GMT</InlineEditable>
              </li>
              {visibleSocials.length > 0 && (
                <li className="flex flex-wrap gap-2 mt-4" data-testid="footer-socials">
                  {visibleSocials.map((p) => {
                    const Icon = p.Icon;
                    return (
                      <a
                        key={p.key}
                        href={socialLinks[p.key].url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={p.label}
                        title={p.label}
                        className="w-9 h-9 rounded-full border border-[hsl(var(--gold)/0.3)] flex items-center justify-center hover:bg-gold hover:!text-rolex-green hover:border-gold transition-all duration-200"
                      >
                        <Icon className="w-4 h-4" />
                      </a>
                    );
                  })}
                </li>
              )}
            </ul>
          </div>
        </div>
        <Separator className="my-10 bg-[hsl(var(--gold)/0.18)]" />
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-cream/45">
          <p>&copy; {new Date().getFullYear()} <span className="text-gold font-semibold tracking-wide">WINLUXOR</span> — <InlineEditable tKey="allRightsReserved">{t('allRightsReserved')}</InlineEditable></p>
          <p className="text-center sm:text-right"><InlineEditable tKey="ageDisclaimer" multiline>{t('ageDisclaimer')}</InlineEditable></p>
        </div>
      </div>
    </footer>
  );
}
